'use client'

import React, { useEffect, useLayoutEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import { ReactAlert } from '@/components/alert'
import { themeAtomSet } from '@/components/apps/Freeze'
import SocialBtn from '@/components/btn/SocialBtn'
import SubmitBtn from '@/components/btn/SubmitBtn'
import AuthInput from '@/components/input/AuthInput'
import { errorStyle } from '@/components/input/inputUtil'
import { InPath } from '@/constant/locale'
import { REGISTER_PATHNAME } from '@/constant/pathname'
import { useTranslations } from '@/lib/i18nProvider'
import { putOwnerUser } from '@/model/dexie/ownerDexie'
import { saveUserDataToDexie, tokenUpdate } from '@/model/dexie/userDexie'
import { signInSchema, SignInType } from '@/model/zod/authZod'
import { LinkedBusinessRefresh, signinEmail } from '@/repository/auth'

import styles from './LoginForm.module.scss'

type Props = {
	locale: string
}
type ErrorMsgs = InPath<'errorText'>

const LoginForm = ({ locale }: Props) => {
	const t = useTranslations('authText.login')
	const e = useTranslations(`errorText`)

	const socialText = ['Apple', 'Google'] as const

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors, isValid, isSubmitting },
	} = useForm<SignInType>({ resolver: zodResolver(signInSchema), mode: 'onChange', criteriaMode: 'all' })

	const emailRegister = register('email')
	const passwordRegister = register('password')

	const router = useRouter()

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
	}, [])

	const submitForm: SubmitHandler<SignInType> = async (formData) => {
		const loginData = {
			email: formData.email,
			password: formData.password,
		}

		const { data: signinEmailResData, response: signinEmailRes, error } = await signinEmail(loginData)

		// TODO : 나중에 any 없애고 타입 받아서 넣기

		if (signinEmailRes && signinEmailResData?.data) {
			saveUserDataToDexie(signinEmailResData.data)
			putOwnerUser()
			tokenUpdate(signinEmailRes)
			LinkedBusinessRefresh()
			router.push('/')
		} else if (error) {
			//@ts-ignore
			let errorMessage = error?.error

			ReactAlert(e(`${errorMessage as ErrorMsgs}`))
		}
	}

	return (
		<div className={styles.area}>
			<div className={styles.space}>
				<div className={styles.wrap}>
					<div className={styles.title}>
						<Link href={'/' + locale} className={styles.logo}>
							<Image alt="Logo" src="/icon/svgs/Logo.svg" fill></Image>
						</Link>
						<div className={styles.line1}>{t('title')}</div>
						<div className={styles.line2}>
							<span>{t('sub')}</span>
							<Link href={REGISTER_PATHNAME(locale)} className={styles.link}>
								{t('link')}
							</Link>
						</div>
					</div>

					<div className={styles.frame}>
						<form onSubmit={handleSubmit(submitForm)} className={styles.form}>
							<AuthInput
								required
								errors={errors}
								keyName="email"
								label={t('label.email')}
								{...emailRegister}
								className={errorStyle(errors.email?.message?.toString())}
								type="email"
								autoComplete="username"
								placeholder={t('label.email')}
								setValue={setValue}
								getValues={getValues}
							/>
							<AuthInput
								required
								errors={errors}
								keyName="password"
								label={t('label.password')}
								{...passwordRegister}
								className={errorStyle(errors.password?.message?.toString())}
								type="password"
								autoComplete="current-password"
								placeholder={t('label.password')}
								setValue={setValue}
								getValues={getValues}
							/>

							{/* TODO : 잠시 보류. 나중에 살리기 */}
							{/* <div className={clc('fdr xBetween')}>
								<div className={styles.maintainArea}>
									<LabelInput type="checkbox" frameClass={styles.icon} style={{ display: 'none' }}>
										<Image alt="Maintain" src="/icon/svgs/auth/Maintain.svg" fill className={styles.maintain} />
									</LabelInput>
									
								</div>

								<div className={clc('fdr mid', styles.find)}>
									<Link href={'/'} className={styles.findLink}>
										이메일 찾기
									</Link>
									<div className={styles.bar}>
										<Image alt="Bar" src="/icon/svgs/auth/Bar.svg" fill />
									</div>
									<Link href={'/'} className={styles.findLink}>
										비밀번호 찾기
									</Link>
								</div>
							</div> */}

							<div className={styles.submitArea}>
								<SubmitBtn text={t('title')} type="submit" isValid={isValid} isSubmitting={isSubmitting} />
							</div>

							<div className={styles.frame}>
								<span className={styles.sns}>{t('sns')}</span>
								<div className={styles.socials}>
									{socialText.map((social) => (
										<SocialBtn key={social} authType={'login'} social={social} />
									))}
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	)
}

export default LoginForm
